import React, { useState } from 'react';
import { useMutation, useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { Id } from '../../../convex/_generated/dataModel';

interface MeterReadingRequestProps {
  userId: Id<"users">;
  hasPushToken: boolean;
}

const STATUS_TEXT: Record<string, string> = {
  open: 'Waiting for the customer',
  fulfilled: 'Reading received',
  cancelled: 'Cancelled',
  expired: 'No response after two reminders',
};

const formatDate = (timestamp: number) =>
  new Date(timestamp).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

// Ask one customer for a generation meter reading. They get an app
// notification that opens the camera, and are reminded weekly (twice) until
// they send one.
const MeterReadingRequest: React.FC<MeterReadingRequestProps> = ({ userId, hasPushToken }) => {
  const latest = useQuery(api.meterReadingRequests.adminGetLatestForUser, { userId });
  const requestReading = useMutation(api.meterReadingRequests.adminRequestForUser);
  const cancelRequest = useMutation(api.meterReadingRequests.adminCancel);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isOpen = latest?.status === 'open';

  const run = async (action: () => Promise<{ success: boolean; error?: string }>) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await action();
      if (!result.success) setError(result.error || 'That did not work. Please try again.');
    } catch (err) {
      console.error('Meter reading request failed:', err);
      setError('That did not work. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-medium">Request a meter reading</p>
          <p className="text-sm text-gray-600">
            {latest
              ? `${STATUS_TEXT[latest.status] ?? latest.status}. Requested ${formatDate(latest.requestedAt)}` +
                (latest.remindersSent ? `, ${latest.remindersSent} reminder${latest.remindersSent === 1 ? '' : 's'} sent.` : '.')
              : hasPushToken
                ? 'Sends a push that opens the app on the camera, then reminds weekly.'
                : 'No phone registered for push: the request will wait in their app inbox.'}
          </p>
        </div>
        {isOpen ? (
          <button
            onClick={() => run(() => cancelRequest({ requestId: latest._id }))}
            disabled={busy}
            className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 disabled:opacity-50"
          >
            Cancel request
          </button>
        ) : (
          <button
            onClick={() => run(() => requestReading({ userId }))}
            disabled={busy || latest === undefined}
            className="px-4 py-2 bg-[#16A34A] text-white rounded-md hover:bg-shade-green-700 disabled:opacity-50"
          >
            {busy ? 'Sending...' : 'Request reading'}
          </button>
        )}
      </div>
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
    </div>
  );
};

export default MeterReadingRequest;
